import { api } from '@common';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { useAuth } from '@hooks';

// Interface for Product Document
export interface ProductDocument {
  id: string;
  productId: string;
  name: string;
  description?: string;
  fileUrl: string;
  createdAt: Date;
}

// Interface for Product Variant
export interface ProductVariant {
  id: string;
  productId: string;
  sku: string;
  size: string;
  chainSize?: string | null;
  price: number;
  stockQuantity: number;
  createdAt: Date;
  updatedAt: Date;
}

// Interface for creating/updating product variants
export interface CreateProductVariantInput {
  sku: string;
  size: string;
  chainSize?: string | null;
  price: number;
  stockQuantity?: number;
}

// Interface for Product
export interface Product {
  id: string;
  name: string;
  description: string;
  imageUrl?: string | null;
  images?: string[];
  videoUrl?: string | null;
  category?: string | null;
  sizes: string[]; // Tamanhos disponíveis (obrigatório)
  chainSizes?: string[]; // Grossuras de corrente (opcional)
  variants?: ProductVariant[];
  documents?: ProductDocument[];
  relatedProducts?: Product[];
  createdAt: Date;
  updatedAt: Date;
}

// Interface for products response (paginated)
interface ProductsResponse {
  data: Product[];
  pagination: {
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
}

// Interface for Product Document metadata when creating/updating
export interface ProductDocumentMetadata {
  name: string;
  description?: string;
}

// Interface for creating/updating products
export interface CreateProductInput {
  name: string;
  description: string;
  imageUrl?: string;
  images?: File[];
  videoUrl?: string;
  category?: string;
  sizes: string[]; // Obrigatório
  chainSizes?: string[];
  variants?: CreateProductVariantInput[];
  documents?: File[];
  documentsMetadata?: ProductDocumentMetadata[];
}

export interface UpdateProductInput {
  name?: string;
  description?: string;
  imageUrl?: string;
  images?: File[];
  videoUrl?: string;
  category?: string;
  sizes?: string[];
  chainSizes?: string[];
  variants?: CreateProductVariantInput[];
  documents?: File[];
  documentsMetadata?: ProductDocumentMetadata[];
}

// Interface for product query parameters
export interface ProductQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  category?: string;
  orderBy?: string;
  orderType?: 'asc' | 'desc';
}

// Hook for fetching paginated products (for admin management)
export const getProducts = (params: ProductQueryParams) => {
  const { isAuthenticated } = useAuth();
  
  return useQuery<ProductsResponse>({
    queryKey: ['products', 'paginated', params],
    queryFn: async (): Promise<ProductsResponse> => {
      try {
        const response = await api.get<ProductsResponse>('/products', { params });
        
        if (response.data) {
          // Process data for frontend use
          const processedData = {
            ...response.data,
            data: response.data.data.map(product => ({
              ...product,
              imageUrl: product.imageUrl || undefined
            }))
          };
          return processedData;
        }
        
        return { data: [], pagination: { totalCount: 0, page: 1, pageSize: 10, totalPages: 0 } };
      } catch (error) {
        console.error('Error retrieving products:', error);
        message.error('Error loading products');
        throw error;
      }
    },
    enabled: isAuthenticated,
  });
};

export const useProducts = () => {
  const queryClient = useQueryClient();

  // Fetch all products (for public display)
  const products = useQuery<Product[], Error>({
    queryKey: ['products'],
    queryFn: async (): Promise<Product[]> => {
      try {
        // Get all products with a large limit
        const response = await api.get<ProductsResponse>('/products', {
          params: {
            limit: 100, // Get up to 100 products
            page: 1
          }
        });
        
        if (response.data && response.data.data) {
          // Process products for frontend use
          return response.data.data.map(product => ({
            ...product,
            imageUrl: product.imageUrl || undefined
          }));
        }
        
        return [];
      } catch (error) {
        console.error('Error retrieving products:', error);
        message.error('Error loading products');
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });

  // Get a specific product by ID
  const getProduct = (id: string) => {
    return useQuery<Product>({
      queryKey: ['product', id],
      queryFn: async () => {
        try {
          const response = await api.get(`/products/${id}`);
          const product = response.data;
          return {
            ...product,
            imageUrl: product.imageUrl || undefined
          };
        } catch (error) {
          console.error(`Error fetching product ${id}:`, error);
          message.error('Unable to load product data');
          throw error;
        }
      },
      enabled: !!id,
    });
  };

  // Create a new product
  const addProduct = useMutation({
    mutationFn: async (productData: CreateProductInput) => {
      try {
        const formData = new FormData();
        
        // Add product fields to FormData
        formData.append('name', productData.name);
        formData.append('description', productData.description);
        
        if (productData.imageUrl) {
          formData.append('imageUrl', productData.imageUrl);
        }
        
        if (productData.videoUrl) {
          formData.append('videoUrl', productData.videoUrl);
        }
        
        if (productData.category) {
          formData.append('category', productData.category);
        }

        // Add sizes and chain sizes as JSON (always send arrays, even if empty)
        formData.append('sizes', JSON.stringify(productData.sizes || []));
        formData.append('chainSizes', JSON.stringify(productData.chainSizes || []));
        
        // Add variants as JSON (always send array, even if empty)
        formData.append('variants', JSON.stringify(productData.variants || []));
        
        // Add multiple images
        if (productData.images && productData.images.length > 0) {
          productData.images.forEach((image) => {
            formData.append('images', image);
          });
        }
        
        // Add multiple documents
        if (productData.documents && productData.documents.length > 0) {
          productData.documents.forEach((document) => {
            formData.append('documents', document);
          });
        }
        
        // Add documents metadata as JSON
        if (productData.documentsMetadata && productData.documentsMetadata.length > 0) {
          formData.append('documentsMetadata', JSON.stringify(productData.documentsMetadata));
        }

        const response = await api.post('/products', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        return response.data;
      } catch (error: any) {
        console.error('Error creating product:', error);
        
        if (error.response?.data?.message) {
          const errorMsg = Array.isArray(error.response.data.message)
            ? error.response.data.message.join(', ')
            : error.response.data.message;
          message.error(`Unable to create product: ${errorMsg}`);
        } else {
          message.error('Unable to create product');
        }
        
        throw error;
      }
    },
    onSuccess: () => {
      message.success('Product created successfully!');
      queryClient.invalidateQueries({ queryKey: ['products'] });
    }
  });

  // Update a product
  const updateProduct = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateProductInput }) => {
      try {
        const formData = new FormData();
        
        // Add product fields to FormData (excluding File arrays and complex objects)
        Object.entries(data).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (key !== 'images' && key !== 'documents' && key !== 'documentsMetadata' && key !== 'sizes' && key !== 'chainSizes' && key !== 'variants') {
              formData.append(key, value.toString());
            }
          }
        });

        // Add sizes and chain sizes as JSON (always send arrays, even if empty)
        if (data.sizes !== undefined) {
          formData.append('sizes', JSON.stringify(data.sizes || []));
        }

        if (data.chainSizes !== undefined) {
          formData.append('chainSizes', JSON.stringify(data.chainSizes || []));
        }

        // Add variants as JSON (always send array, even if empty)
        if (data.variants !== undefined) {
          formData.append('variants', JSON.stringify(data.variants || []));
        }
        
        // Add multiple images
        if (data.images && data.images.length > 0) {
          data.images.forEach((image) => {
            formData.append('images', image);
          });
        }
        
        // Add multiple documents
        if (data.documents && data.documents.length > 0) {
          data.documents.forEach((document) => {
            formData.append('documents', document);
          });
        }
        
        // Add documents metadata as JSON
        if (data.documentsMetadata && data.documentsMetadata.length > 0) {
          formData.append('documentsMetadata', JSON.stringify(data.documentsMetadata));
        }

        const response = await api.patch(`/products/${id}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        return response.data;
      } catch (error) {
        console.error('API error updating product:', error);
        message.error('Error updating product');
        throw error;
      }
    },
    onSuccess: (_data, variables) => {
      message.success('Product updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product', variables.id] });
    }
  });

  // Delete a product
  const deleteProduct = useMutation({
    mutationFn: async (productId: string) => {
      try {
        const response = await api.delete(`/products/${productId}`);
        return response.data;
      } catch (error) {
        message.error('Error deleting product');
        throw error;
      }
    },
    onSuccess: (_, productId) => {
      message.success('Product deleted successfully!');
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product', productId] });
    }
  });

  // Get variant price with tier discount
  const getVariantPrice = async (productId: string, size: string, chainSize: string | null, tier: string = 'retail') => {
    try {
      const response = await api.get(`/products/${productId}/variants/price`, {
        params: { size, chainSize, tier }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching variant price:', error);
      throw error;
    }
  };

  // Create a variant for a product
  const createVariant = useMutation({
    mutationFn: async ({ productId, data }: { productId: string; data: CreateProductVariantInput }) => {
      try {
        const response = await api.post(`/products/${productId}/variants`, data);
        return response.data;
      } catch (error: any) {
        console.error('Error creating variant:', error);
        if (error.response?.data?.message) {
          message.error(`Unable to create variant: ${error.response.data.message}`);
        } else {
          message.error('Unable to create variant');
        }
        throw error;
      }
    },
    onSuccess: (_, variables) => {
      message.success('Variant created successfully!');
      queryClient.invalidateQueries({ queryKey: ['product', variables.productId] });
    }
  });

  // Update a variant
  const updateVariant = useMutation({
    mutationFn: async ({ variantId, data, productId: _productId }: { variantId: string; data: Partial<CreateProductVariantInput>; productId: string }) => {
      void _productId; // Used in onSuccess
      try {
        const response = await api.patch(`/products/variants/${variantId}`, data);
        return response.data;
      } catch (error) {
        console.error('Error updating variant:', error);
        message.error('Error updating variant');
        throw error;
      }
    },
    onSuccess: (_, variables) => {
      message.success('Variant updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['product', variables.productId] });
    }
  });

  // Delete a variant
  const deleteVariant = useMutation({
    mutationFn: async ({ variantId, productId: _productId }: { variantId: string; productId: string }) => {
      void _productId; // Used in onSuccess
      try {
        const response = await api.delete(`/products/variants/${variantId}`);
        return response.data;
      } catch (error) {
        console.error('Error deleting variant:', error);
        message.error('Error deleting variant');
        throw error;
      }
    },
    onSuccess: (_, variables) => {
      message.success('Variant deleted successfully!');
      queryClient.invalidateQueries({ queryKey: ['product', variables.productId] });
    }
  });

  return {
    products,
    getProduct,
    addProduct,
    updateProduct,
    deleteProduct,
    getVariantPrice,
    createVariant,
    updateVariant,
    deleteVariant,
  };
};

// Tier discounts helper
export const TIER_DISCOUNTS: Record<string, number> = {
  tier1: 0.12,  // 12% off
  tier2: 0.09,  // 9% off
  tier3: 0.05,  // 5% off
  retail: 0,    // full price
};

// Calculate price with tier discount
export const calculatePriceWithTier = (basePrice: number, tier: string): number => {
  const discount = TIER_DISCOUNTS[tier] || 0;
  return Number((basePrice * (1 - discount)).toFixed(2));
};

// Get tier discount percentage
export const getTierDiscountPercent = (tier: string): number => {
  return (TIER_DISCOUNTS[tier] || 0) * 100;
};

